import { LineChart, ListChecks, Settings2, Target, Trash2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const ICON_COLORS = {
  diagnosis: 'text-rose-600',
  goals: 'text-blue-600',
  interventions: 'text-emerald-600',
  evaluation: 'text-purple-600',
};

function EditableSection({ icon: Icon, title, tone, value, onChange }) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 shrink-0 ${ICON_COLORS[tone]}`} strokeWidth={2} />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <textarea
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary"
      />
    </section>
  );
}

export default function CarePlanNote({ note, onChange, allowDelete = true }) {
  const { t } = useLanguage();
  const plan = note?.care_plan || {};
  const interventions = Array.isArray(plan.interventions) ? plan.interventions : [];

  const updatePlan = (update) => {
    onChange({
      ...note,
      care_plan: {
        ...plan,
        ...update,
      },
    });
  };

  const updateIntervention = (index, value) => {
    const next = [...interventions];
    next[index] = value;
    updatePlan({ interventions: next });
  };

  const removeIntervention = (index) => {
    updatePlan({ interventions: interventions.filter((_, itemIndex) => itemIndex !== index) });
  };

  return (
    <div className="space-y-4">
      <EditableSection
        icon={Target}
        title={t('nursingDiagnosis')}
        tone="diagnosis"
        value={plan.nursing_diagnosis}
        onChange={(value) => updatePlan({ nursing_diagnosis: value })}
      />

      <EditableSection
        icon={ListChecks}
        title={t('carePlanGoals')}
        tone="goals"
        value={plan.goals}
        onChange={(value) => updatePlan({ goals: value })}
      />

      <section className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Settings2 className={`h-4 w-4 shrink-0 ${ICON_COLORS.interventions}`} strokeWidth={2} />
          <h3 className="text-sm font-semibold text-gray-900">{t('carePlanInterventions')}</h3>
        </div>
        <div className="space-y-2">
          {interventions.map((intervention, index) => (
            <div key={`care-plan-intervention-${index}`} className="flex items-start gap-2">
              <textarea
                value={intervention}
                onChange={(event) => updateIntervention(index, event.target.value)}
                rows={2}
                className="min-w-0 flex-1 resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary"
              />
              {allowDelete ? (
                <button
                  type="button"
                  onClick={() => removeIntervention(index)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
                  aria-label={t('deleteIntervention')}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={2} />
                </button>
              ) : null}
            </div>
          ))}
          <button
            type="button"
            onClick={() => updatePlan({ interventions: [...interventions, ''] })}
            className="rounded-full border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800"
          >
            + {t('addIntervention')}
          </button>
        </div>
      </section>

      <EditableSection
        icon={LineChart}
        title={t('carePlanEvaluation')}
        tone="evaluation"
        value={plan.evaluation}
        onChange={(value) => updatePlan({ evaluation: value })}
      />
    </div>
  );
}
